import type { useAmelioTranslations } from '@/hooks';
import type { TranslationKey } from '@/i18n-key';
import type { Reviewee } from '@/utils/reviewEvaluatorSelection';

import {
    ANONYMOUS_MINIMUM_REFUSAL,
    meetsAnonymousMinimum,
    type FeedbackPrivacyValue,
} from '@/constants/review/FeedbackPrivacy';
import { formatNameList } from '@/utils/nameList';
import { getFullName } from '@/utils/user';

interface ReviewersErrorMessageParams {
    messageKey?: TranslationKey;
    reviewees: Reviewee[];
    feedbackPrivacy: FeedbackPrivacyValue;
    t: ReturnType<typeof useAmelioTranslations>['t'];
    language: string;
}

/**
 * Why "Next" refused the reviewers. With several reviewees, the anonymous minimum also names those it
 * stops, who may be far down the page.
 */
export const getReviewersErrorMessage = ({
    messageKey,
    reviewees,
    feedbackPrivacy,
    t,
    language,
}: ReviewersErrorMessageParams): string | undefined => {
    if (!messageKey) {
        return undefined;
    }

    const message = t(messageKey, ANONYMOUS_MINIMUM_REFUSAL.values);
    if (messageKey !== ANONYMOUS_MINIMUM_REFUSAL.messageKey || reviewees.length <= 1) {
        return message;
    }

    const namesUnderMinimum = reviewees
        .filter(
            ({ reviewers }) =>
                !meetsAnonymousMinimum({ feedbackPrivacy, respondentCount: reviewers.length }),
        )
        .map((reviewee) => getFullName(reviewee));
    const missingFor = t('feedbackPrivacy.anonymousMinimumMissingFor', {
        names: formatNameList(namesUnderMinimum, language),
    });

    return `${message} ${missingFor}`;
};
